import { useEffect, useRef } from 'react'
import { authUtils } from '@/lib/api/auth'
import { msUntilAccessTokenExpiry, refreshAccessToken } from '@/lib/api/refresh'

/**
 * Schedule JWT access-token refresh based on the token's actual `exp` claim.
 *
 * - Refreshes at `exp - REFRESH_LEAD_MS`.
 * - On `visibilitychange` (tab becomes visible), refreshes immediately if the
 *   token is already within the refresh window or expired (covers the case
 *   where `setTimeout` was throttled while the tab was backgrounded).
 * - Reschedules against the new token's `exp` after each successful refresh.
 *
 * Must be mounted once at the app/auth-provider level so it runs for the
 * entire authenticated session, not just on a single page.
 */

// Refresh this many ms before the access token actually expires.
const REFRESH_LEAD_MS = 60 * 1000

export function useTokenRefresh() {
  const timeoutRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (!authUtils.isAuthenticated()) return

    let cancelled = false

    const clearTimer = () => {
      if (timeoutRef.current !== undefined) {
        window.clearTimeout(timeoutRef.current)
        timeoutRef.current = undefined
      }
    }

    const scheduleNext = () => {
      clearTimer()
      const msToExpiry = msUntilAccessTokenExpiry()
      if (msToExpiry === null) return // no token — nothing to schedule

      // Fire REFRESH_LEAD_MS before expiry, but never more than 24h out
      // and never less than 0. If already inside the lead window, fire ASAP.
      const delay = Math.min(Math.max(msToExpiry - REFRESH_LEAD_MS, 0), 24 * 60 * 60 * 1000)
      console.log(`[TokenRefresh] next refresh in ${Math.round(delay / 1000)}s`)
      timeoutRef.current = window.setTimeout(runRefresh, delay)
    }

    const runRefresh = async () => {
      if (cancelled) return
      const msLeft = msUntilAccessTokenExpiry()
      console.log(
        `[TokenRefresh] refreshing access token… (${msLeft !== null ? Math.round(msLeft / 1000) : '?'}s until expiry)`,
      )
      const newToken = await refreshAccessToken()
      if (cancelled) return
      if (newToken) {
        console.log('[TokenRefresh] ✓ refreshed')
        scheduleNext()
      } else {
        console.warn('[TokenRefresh] ✗ refresh failed; session will end on next 401')
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      const msToExpiry = msUntilAccessTokenExpiry()
      if (msToExpiry === null) return
      // If we're inside the refresh window (including already expired), refresh now.
      if (msToExpiry <= REFRESH_LEAD_MS) {
        runRefresh()
      } else {
        // Otherwise, reschedule in case the timer was throttled while hidden.
        scheduleNext()
      }
    }

    scheduleNext()
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      cancelled = true
      clearTimer()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])
}
